import React, { useEffect, useMemo, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import Navbar from './NavBar.js';
import Footer from './Footer.js';
import '../styles/ServiceDetail.css';
import { useServices } from '../../ViewModel/ServiceViewModel.js';
import { serviceService } from '../../Model/Services/serviceService.js';
import { useRecaptchaV2 } from './useRecaptchaV2.js';

const FALLBACK_SERVICE_IMAGE = '/Images/works.jpg';

const DEFAULT_INCLUDED_INTRO =
  'Vi tar ansvar för hela processen så att du kan fokusera på verksamheten. ';
const DEFAULT_INCLUDED_ITEMS = [
  'Upprättande av komplett årsredovisning enligt gällande regelverk ',
  'Rådgivning om vinstdisposition och utdelningsmöjligheter',
  'Dedikerad kontaktperson som följer dig genom hela processen',
  'Tydliga leveranser och uppföljning',
];

const DEFAULT_FITS_INTRO = 'Tjänsten är perfekt för dig som vill: ';
const DEFAULT_FITS_ITEMS = [
  'Slippa stressen kring bokslut och deadlines ',
  'Vara säker på att allt blir korrekt och i tid ',
  'Frigöra tid och få bättre koll över ekonomin ',
  'Ha en proaktiv partner som ger råd, inte bara producerar deklarationer',
];

function resolveDetailPanel(intro, items, defaultIntro, defaultItems) {
  const trimmedIntro = (intro || '').trim();
  const list = Array.isArray(items) ? items.map(String).filter(Boolean) : [];
  if (!trimmedIntro && list.length === 0) {
    return { intro: defaultIntro, items: defaultItems };
  }
  return { intro: trimmedIntro, items: list };
}
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const MIN_SUBMIT_DELAY_MS = 3000;
const SUBMIT_COOLDOWN_MS = 30000;
const SERVICE_CONTACT_LAST_SENT_KEY = 'serviceContactLastSentAt';

function ServiceDetail({ serviceId }) {
  // Resolve from in-memory list first for instant navigation, then fall back to direct fetch.
  const { services, loading: servicesLoading } = useServices();
  const normalizedId = useMemo(() => String(serviceId), [serviceId]);
  const numericId = useMemo(() => {
    const parsed = Number(serviceId);
    return Number.isNaN(parsed) ? serviceId : parsed;
  }, [serviceId]);
  const [service, setService] = useState(() => services.find((svc) => String(svc.id) === normalizedId));
  const [loading, setLoading] = useState(!service);
  const [error, setError] = useState('');
  const [selectedServiceTitle, setSelectedServiceTitle] = useState(service?.title || '');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [miniStatus, setMiniStatus] = useState({ type: '', message: '' });
  const [isSendingMini, setIsSendingMini] = useState(false);
  const miniFormStartedAtRef = useRef(Date.now());
  const { containerRef: recaptchaContainerRef, getToken, reset: resetRecaptcha } = useRecaptchaV2(RECAPTCHA_SITE_KEY);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [normalizedId]);

  useEffect(() => {
    const match = services.find((svc) => String(svc.id) === normalizedId);
    if (match) {
      setService(match);
      setSelectedServiceTitle(match.title || '');
      setLoading(false);
      setError('');
      return;
    }

    if (servicesLoading) {
      setLoading(true);
      return;
    }

    let active = true;
    const fetchService = async () => {
      setLoading(true);
      try {
        const fetched = await serviceService.getById(numericId);
        if (!active) return;
        setService(fetched);
        setSelectedServiceTitle(fetched?.title || '');
        setError('');
      } catch (err) {
        if (!active) return;
        setError('Kunde inte hitta tjänsten just nu.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchService();
    return () => {
      active = false;
    };
  }, [services, normalizedId, servicesLoading, numericId]);

  const navigateTo = (targetPath) => {
    window.history.pushState({}, '', targetPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContact = () => navigateTo('/kontakt');
  const handleBack = () => navigateTo('/#tjanster');

  if (loading) {
    return (
      <div className="service-detail-page">
        <Navbar />
        <main className="service-detail-main service-detail-main--loading">
          <div className="service-skeleton service-skeleton--hero" />
          <div className="service-skeleton service-skeleton--body" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-detail-page">
        <Navbar />
        <main className="service-detail-main">
          <section className="service-error">
            <p className="service-error_label">Våra tjänster</p>
            <h1>Vi kunde inte ladda tjänsten</h1>
            <p className="service-error_text">{error || 'Försök igen eller gå tillbaka till startsidan.'}</p>
            <div className="service-error_actions">
              <button className="detail-btn" onClick={handleBack}>Tillbaka</button>
              <button className="detail-btn detail-btn--primary" onClick={handleContact}>Kontakta oss</button>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const heroImage = service.image || FALLBACK_SERVICE_IMAGE;
  const ctaLabel = service.buttonCTA || 'Kontakta oss';
  const serviceOptions = services && services.length ? services : [service];

  const includedPanel = resolveDetailPanel(
    service.includedIntro,
    service.includedItems,
    DEFAULT_INCLUDED_INTRO,
    DEFAULT_INCLUDED_ITEMS,
  );
  const fitsPanel = resolveDetailPanel(
    service.fitsIntro,
    service.fitsItems,
    DEFAULT_FITS_INTRO,
    DEFAULT_FITS_ITEMS,
  );

  const handleMiniFormSubmit = async (event) => {
    event.preventDefault();
    setMiniStatus({ type: '', message: '' });

    // Simple anti-spam guardrail: hidden honeypot + minimum fill time + short cooldown.
    if (website.trim()) {
      setMiniStatus({ type: 'error', message: 'Kunde inte skicka just nu. Försök igen senare.' });
      return;
    }

    if (Date.now() - miniFormStartedAtRef.current < MIN_SUBMIT_DELAY_MS) {
      setMiniStatus({ type: 'error', message: 'Vänta några sekunder och försök igen.' });
      return;
    }

    try {
      const lastSent = Number(sessionStorage.getItem(SERVICE_CONTACT_LAST_SENT_KEY) || '0');
      if (lastSent && Date.now() - lastSent < SUBMIT_COOLDOWN_MS) {
        setMiniStatus({ type: 'error', message: 'Vänta en stund innan du skickar igen.' });
        return;
      }
    } catch (_) {
      // ignore storage errors
    }

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setMiniStatus({ type: 'error', message: 'E-postinställningar saknas. Kontakta admin.' });
      return;
    }

    if (!RECAPTCHA_SITE_KEY) {
      setMiniStatus({ type: 'error', message: 'reCAPTCHA saknas. Kontakta admin.' });
      return;
    }

    const recaptchaToken = getToken();
    if (!recaptchaToken) {
      setMiniStatus({ type: 'error', message: 'Bekräfta reCAPTCHA innan du skickar.' });
      return;
    }

    setIsSendingMini(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: selectedServiceTitle || 'Serviceförfrågan',
          from_email: email,
          phone,
          orgnr: '',
          topic: selectedServiceTitle || 'Serviceförfrågan',
          message: `Intresse för tjänst: ${selectedServiceTitle || service.title || 'Okänd'}. Telefon: ${phone || 'Ej angivet'}. Källa: mini-formulär på tjänstesidan.`,
          'g-recaptcha-response': recaptchaToken,
        },
        { publicKey: EMAILJS_PUBLIC_KEY },
      );

      setMiniStatus({ type: 'success', message: 'Tack! Vi återkommer så snart vi kan.' });
      setPhone('');
      setEmail('');
      setWebsite('');
      miniFormStartedAtRef.current = Date.now();
      resetRecaptcha();
      try {
        sessionStorage.setItem(SERVICE_CONTACT_LAST_SENT_KEY, String(Date.now()));
      } catch (_) {
        // ignore storage errors
      }
    } catch (err) {
      setMiniStatus({ type: 'error', message: 'Kunde inte skicka just nu. Försök igen eller mejla oss direkt.' });
    } finally {
      setIsSendingMini(false);
    }
  };

  return (
    <div className="service-detail-page">
      <Navbar />
      <main className="service-detail-main">
        <section className="service-hero">
          <div className="service-hero_bg" style={{ backgroundImage: `url(${heroImage})` }} />
          <div className="service-hero_overlay" />
          <div className="service-hero_content">
            <div className="hero-form">
              <h2>Vi finns här för dig.</h2>
              <p className="hero-form_lede">Fyll i dina uppgifter och skicka in formuläret så kontaktar vi dig snarast.</p>
              <form className="hero-form_grid" onSubmit={handleMiniFormSubmit}>
                <label className="hero-form_field">
                  <span className="hero-form_label">Vad kan vi hjälpa dig med?</span>
                  <select
                    value={selectedServiceTitle}
                    onChange={(e) => setSelectedServiceTitle(e.target.value)}
                    required
                  >
                    {serviceOptions.map((opt) => (
                      <option key={opt?.id || opt?.title} value={opt?.title || ''}>
                        {opt?.title || ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="hero-form_field">
                  <span className="hero-form_label">Telefonnummer</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    pattern="[0-9+\- ]{6,}"
                    placeholder="070 123 45 67"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={40}
                    required
                  />
                </label>
                <label className="hero-form_field">
                  <span className="hero-form_label">E-post</span>
                  <input
                    type="email"
                    placeholder="du@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={200}
                    required
                  />
                </label>
                <label className="sr-only" aria-hidden="true">
                  Lämna detta fält tomt
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </label>
                {RECAPTCHA_SITE_KEY ? (
                  <div className="recaptcha-row">
                    <div ref={recaptchaContainerRef} />
                  </div>
                ) : (
                  <p className="muted small">reCAPTCHA är inte konfigurerat.</p>
                )}
                <div className="hero-form_actions">
                  <button type="submit" className="detail-btn detail-btn--primary" disabled={isSendingMini}>
                    {isSendingMini ? 'Skickar...' : 'Skicka'}
                  </button>
                </div>
                {miniStatus.message && (
                  <p className={`hero-form_success ${miniStatus.type === 'error' ? 'hero-form_error' : ''}`}>
                    {miniStatus.message}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>

        <section className="service-layout">
          <div className="service-layout_main">
            <div className="chip">Personlig rådgivning</div>
            <h2>{service.title}</h2>
            <p className="lede">{service.content}</p>
            <div className="service-panels">
              <div className="service-panel">
                <h3>Vad ingår</h3>
                {includedPanel.intro ? <p>{includedPanel.intro}</p> : null}
                {includedPanel.items.length > 0 ? (
                  <ul>
                    {includedPanel.items.map((line, idx) => (
                      <li key={`included-${idx}`}>{line}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className="service-panel">
                <h3>När passar tjänsten</h3>
                {fitsPanel.intro ? <p>{fitsPanel.intro}</p> : null}
                {fitsPanel.items.length > 0 ? (
                  <ul>
                    {fitsPanel.items.map((line, idx) => (
                      <li key={`fits-${idx}`}>{line}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </div>

          <aside className="service-layout_side">
            <div className="info-card">
              <p className="info-card_label">Nästa steg</p>
              <h3>Vi gör det enkelt att starta</h3>
              <p>Berätta kort om ditt behov så återkommer vi snabbt med en plan och tydliga steg framåt.</p>
              <div className="info-card_actions">
                <button className="detail-btn detail-btn--primary" onClick={handleContact}>{ctaLabel}</button>
                <button className="detail-btn detail-btn--ghost" onClick={handleBack}>Tillbaka till alla</button>
              </div>
            </div>

            <div className="info-card info-card--muted">
              <p className="info-card_label">Varför oss</p>
              <ul className="info-list">
                <li>Specialister med branschvana</li>
                <li>Snabba svar och tydlig kommunikation</li>
                <li>Transparent prissättning och process</li>
              </ul>
            </div>
          </aside>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default ServiceDetail;
